import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, Box, Button,
  Dialog, DialogContent, DialogActions,
  IconButton, Chip,
} from '@mui/material';
import {
  Close as CloseIcon, ArrowForward as ArrowIcon, Check as CheckIcon,
  PersonAdd as PersonAddIcon, School as SchoolIcon, AccountBox as AccountBoxIcon,
  VolunteerActivism as VolunteerIcon, MenuBook as MenuBookIcon, Groups as GroupsIcon,
  People as PeopleIcon, Timeline as TimelineIcon, Notifications as NotificationsIcon,
} from '@mui/icons-material';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';

// Paleta aprovada (dark/teal/lime)
const TEAL = '#00F1A7';
const LIME = '#B4F300';
const DARK = '#05080F';
const SURFACE = '#0A141C';
const TEXT = '#E9F1EF';
const MUTED = '#93A7A3';
const BORDER = 'rgba(0,241,167,0.12)';
const BORDER_STRONG = 'rgba(0,241,167,0.22)';
const TEAL_SOFT = 'rgba(0,241,167,0.10)';
const LIME_SOFT = 'rgba(180,243,0,0.10)';

const MODULOS = [
  {
    key: 'consolidacao',
    titulo: 'Consolidação',
    subtitulo: 'Visitantes e Novos Contatos',
    icone: PersonAddIcon,
    cor: TEAL,
    corBg: TEAL_SOFT,
    descricao: 'Automatize o acompanhamento de visitantes desde o primeiro contato até a decisão por Cristo.',
    destaque: 'Distribuição inteligente',
    path: '/gestao-visitantes',
    beneficios: [
      'Automatiza o fluxo de novos visitantes desde o primeiro contato',
      'Distribuição inteligente para consolidadores com base em disponibilidade e localização',
      'Acompanhamento em tempo real do progresso da consolidação',
      'Notificações automáticas para consolidadores sobre novos visitantes',
      'Relatórios de desempenho da consolidação para gestores',
      'Migração simplificada de visitante consolidado para aluno do Start',
    ],
    comoFunciona: 'O visitante é cadastrado no sistema e automaticamente distribuído para um consolidador disponível. O consolidador realiza o acompanhamento, registra as visitas e, quando o visitante está pronto, migra o registro para o módulo Start (escola), onde ele se torna um aluno.',
    praQuem: 'Pastores, líderes de consolidação, consolidadores e secretários.',
  },
  {
    key: 'startfy',
    titulo: 'Start',
    subtitulo: 'Gestão Escolar',
    icone: SchoolIcon,
    cor: LIME,
    corBg: LIME_SOFT,
    descricao: 'Controle completo de alunos, turmas, professores, frequência e formandos.',
    destaque: 'Relatórios automáticos',
    path: '/area-startfy',
    beneficios: [
      'Gestão completa de alunos, professores, turmas e períodos letivos',
      'Lançamento de frequência por aula com interface simplificada',
      'Matrícula de alunos em múltiplas turmas',
      'Dashboards com indicadores de desempenho escolar',
      'Relatórios gerenciais exportáveis (PDF)',
      'Módulo de formandos com data de conclusão e histórico',
    ],
    comoFunciona: 'O administrador cadastra professores e alunos, cria turmas vinculadas a períodos letivos e lança aulas com conteúdo programático. A frequência é registrada aula a aula. Ao final do período, alunos com frequência suficiente são formados e podem ser migrados para a Secretaria como membros.',
    praQuem: 'Professores, administradores escolares, secretários de ensino.',
  },
  {
    key: 'secretaria',
    titulo: 'Secretaria',
    subtitulo: 'Cadastro de Membros',
    icone: AccountBoxIcon,
    cor: TEAL,
    corBg: TEAL_SOFT,
    descricao: 'Gestão centralizada de todos os membros da igreja com histórico completo.',
    destaque: 'Migração automática',
    path: '/secretaria',
    beneficios: [
      'Cadastro completo de membros com dados pessoais, contato e documentos',
      'Migração automática de alunos do Start para cadastro de membros',
      'Importação de membros via planilha Excel',
      'Histórico completo de migrações e movimentações',
      'Controle de permissões por tipo de usuário',
    ],
    comoFunciona: 'A Secretaria gerencia o cadastro de todos os membros da igreja. Alunos que concluem o Start podem ser migrados automaticamente para membros, preservando todo o histórico. A importação por Excel permite cadastro em lote de membros já existentes.',
    praQuem: 'Secretários, pastores, administradores.',
  },
  {
    key: 'voluntariado',
    titulo: 'Voluntariado',
    subtitulo: 'Departamentos e Escalas',
    icone: VolunteerIcon,
    cor: LIME,
    corBg: LIME_SOFT,
    descricao: 'Organize voluntários por departamento, crie eventos e gerencie escalas automaticamente.',
    destaque: 'Confirmação online',
    path: '/voluntariado',
    beneficios: [
      'Organização de voluntários por departamentos específicos',
      'Criação de eventos com escala automática de voluntários',
      'Voluntários podem confirmar ou liberar sua participação',
      'Registro de habilidades e disponibilidade de cada voluntário',
      'Agenda individual do voluntário com todos os eventos escalados',
      'Relatório de escala por evento em PDF',
    ],
    comoFunciona: 'O gestor cria departamentos (música, recepção, som, etc.) e cadastra voluntários com suas habilidades. Eventos são criados com data, horário e vagas máximas. O sistema gerencia a escala, disponibilidade e confirmação de cada voluntário.',
    praQuem: 'Líderes de ministérios, pastores, coordenadores de voluntariado.',
  },
  {
    key: 'cursos',
    titulo: 'Cursos',
    subtitulo: 'Treinamentos e Certificados',
    icone: MenuBookIcon,
    cor: TEAL,
    corBg: TEAL_SOFT,
    descricao: 'Crie cursos sazonais ou avulsos, gerencie inscrições, presença e emita certificados PDF.',
    destaque: 'Certificados automáticos',
    path: '/cursos',
    beneficios: [
      'Criação de cursos e treinamentos sazonais ou avulsos',
      'Atribuição de instrutores especializados por sessão',
      'Material didático multimídia: texto, imagem, vídeo YouTube, links e arquivos',
      'Inscrição de membros e também de externos (público geral)',
      'Registro de presença por sessão',
      'Certificado PDF automático ao concluir o curso',
      'Relatório PDF completo do curso',
    ],
    comoFunciona: 'O gestor cria um curso vinculado a um ministério, define datas e vagas. Sessões são criadas com instrutor, local e carga horária. Materiais didáticos são anexados a cada sessão. Alunos se inscrevem, têm presença registrada e ao final recebem certificado.',
    praQuem: 'Pastores, líderes de ministério, instrutores, secretários.',
  },
  {
    key: 'discipulado',
    titulo: 'Discipulado',
    subtitulo: 'Relacionamento e Acompanhamento',
    icone: GroupsIcon,
    cor: LIME,
    corBg: LIME_SOFT,
    descricao: 'Vincule discipulador a discipulando, acompanhe o progresso e identifique quem precisa de atenção.',
    destaque: 'Tracking completo',
    path: '/discipulados',
    beneficios: [
      'Relacionamento claro entre discipulador e discipulando',
      'Acompanhamento do status de cada discipulado (ativo, pausado, concluído)',
      'Identificação de membros que não possuem discipulador',
      'Vinculação de cursos ao discipulado para formação direcionada',
      'Registro de observações e histórico do relacionamento',
    ],
    comoFunciona: 'Cada membro pode ter um discipulador designado. O sistema permite gerenciar esses relacionamentos, acompanhar o status e identificar membros que ainda não têm discipulador. Discipuladores podem criar turmas e vincular cursos ao discipulado.',
    praQuem: 'Pastores, líderes de discipulado, discipuladores.',
  },
  {
    key: 'gcs',
    titulo: 'Grupos de Crescimento',
    subtitulo: 'GCs e Líderes',
    icone: PeopleIcon,
    cor: TEAL,
    corBg: TEAL_SOFT,
    descricao: 'Cadastre GCs com líder e co-líder, gerencie membros e acompanhe indicadores em tempo real.',
    destaque: 'Aprovação do pastor',
    path: '/grupos-crescimento',
    beneficios: [
      'Cadastro completo de GCs com líder e co-líder',
      'Fluxo de aprovação pelo pastor para novos GCs',
      'Gestão de membros por GC com entrada e saída',
      'Dashboard com indicadores: total de GCs, ativos, pendentes, membros',
      'Identificação de membros que não estão em nenhum GC',
      'Relatório PDF geral de todos os GCs',
    ],
    comoFunciona: 'GCs são cadastrados com líder, dia e horário de reunião e endereço. Novos GCs passam por aprovação do pastor. Membros são adicionados aos GCs e o dashboard mostra a situação geral. O sistema alerta sobre membros sem GC.',
    praQuem: 'Pastores, líderes de GC, secretários.',
  },
  {
    key: 'jornada',
    titulo: 'Jornada do Membro',
    subtitulo: 'Timeline Visual',
    icone: TimelineIcon,
    cor: LIME,
    corBg: LIME_SOFT,
    descricao: 'Visualize toda a história do membro: visita, decisão, batismo, discipulado, GC e função atual.',
    destaque: 'Uma tela, tudo',
    path: '/jornada',
    beneficios: [
      'Visão completa da jornada de cada membro em uma timeline',
      'Acompanhamento desde a primeira visita até a função atual',
      'Registro de origem, decisão, batismo e estado civil',
      'Integração com discipulado, GC e cursos frequentados',
      'Registro de filhos com data de nascimento',
      'Timeline editável (arraste para reordenar)',
      'Alertas sobre membros sem acompanhamento e líderes sem discipulador',
    ],
    comoFunciona: 'A Jornada consolida em uma única tela todas as informações de um membro: sua origem na igreja, data de batismo, discipulador, GC, cursos realizados, função atual e filhos. Tudo é visualizado em uma timeline vertical editável.',
    praQuem: 'Pastores, secretários, líderes de ministério.',
  },
  {
    key: 'notificacoes',
    titulo: 'Notificações',
    subtitulo: 'Central de Alertas',
    icone: NotificationsIcon,
    cor: TEAL,
    corBg: TEAL_SOFT,
    descricao: 'Receba alertas automáticos sobre novos membros, eventos, consolidações e muito mais.',
    destaque: 'Sempre informado',
    path: '/notificacoes-centro',
    beneficios: [
      'Notificações automáticas geradas por eventos do sistema',
      'Central de notificações com filtro por tipo',
      'Badge no header com contagem de não lidas',
      'Marcação individual ou em lote como lidas',
      'Links diretos para o contexto da notificação',
    ],
    comoFunciona: 'O sistema gera notificações automaticamente quando eventos importantes ocorrem: novo membro cadastrado, novo GC criado, início de discipulado, migração de etapa, etc. A central de notificações permite visualizar, filtrar e gerenciar todas as notificações.',
    praQuem: 'Todos os usuários do sistema.',
  },
];

export default function Recursos() {
  const navigate = useNavigate();
  const [modal, setModal] = useState({ open: false, modulo: null });

  return (
    <>
      <PublicHeader activePage="Recursos" />

      {/* ── Hero ──────────────────────────────────────────────── */}
      <Box sx={{
        background: 'linear-gradient(150deg, #0A1D26, #05080F)',
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', bgcolor: `${TEAL}08`, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', bgcolor: `${TEAL}06`, pointerEvents: 'none' }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              display: 'inline-block',
              bgcolor: `${TEAL}12`,
              border: `1px solid ${TEAL}40`,
              borderRadius: '5px',
              px: 2,
              py: 0.5,
              mb: 3,
            }}>
              <Typography sx={{ color: TEAL, fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}>
                CONHEÇA O SISTEMA
              </Typography>
            </Box>

            <Typography variant="h1" sx={{
              fontWeight: 800,
              color: '#fff',
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 2,
            }}>
              Todos os recursos que sua<br />
              <Box component="span" sx={{ color: TEAL }}>igreja precisa.</Box>
            </Typography>

            <Typography sx={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: { xs: '1rem', md: '1.15rem' },
              maxWidth: 560,
              mx: 'auto',
              lineHeight: 1.7,
            }}>
              Oito módulos integrados para centralizar a gestão pastoral. Sem planilha, sem WhatsApp, sem confusão.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Cards de Módulos ──────────────────────────────────── */}
      <Box sx={{ bgcolor: DARK, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {MODULOS.map((modulo) => {
              const IconComp = modulo.icone;
              return (
                <Grid item xs={12} sm={6} md={4} key={modulo.key}>
                  <Card sx={{
                    height: '100%',
                    borderRadius: '5px',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    bgcolor: SURFACE,
                    border: `1px solid ${BORDER}`,
                    transition: 'transform 0.2s ease, border-color 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: BORDER_STRONG,
                    },
                  }}>
                    {/* Header com cor */}
                    <Box sx={{
                      bgcolor: modulo.corBg,
                      p: 3,
                      borderBottom: `2px solid ${modulo.cor}`,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '5px',
                          bgcolor: modulo.cor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <IconComp sx={{ color: '#0B1200', fontSize: 28 }} />
                        </Box>
                        <Box sx={{
                          bgcolor: modulo.corBg,
                          border: `1px solid ${modulo.cor}40`,
                          borderRadius: '5px',
                          px: 1.5,
                          py: 0.5,
                        }}>
                          <Typography sx={{ color: modulo.cor, fontSize: '0.7rem', fontWeight: 700 }}>
                            MÓDULO
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Body */}
                    <Box sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: TEXT, mb: 0.5 }}>
                        {modulo.titulo}
                      </Typography>
                      <Typography sx={{ color: modulo.cor, fontWeight: 600, fontSize: '0.85rem', mb: 1.5 }}>
                        {modulo.subtitulo}
                      </Typography>
                      <Typography sx={{ color: MUTED, lineHeight: 1.6, mb: 2 }}>
                        {modulo.descricao}
                      </Typography>
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: modulo.corBg,
                        borderRadius: '5px',
                        px: 1.5,
                        py: 0.5,
                        mb: 2,
                      }}>
                        <Typography sx={{ color: modulo.cor, fontWeight: 700, fontSize: '0.8rem' }}>
                          {modulo.destaque}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          onClick={() => setModal({ open: true, modulo })}
                          sx={{
                            borderColor: TEAL,
                            color: TEAL,
                            fontWeight: 700,
                            borderRadius: '5px',
                            '&:hover': { borderColor: TEAL, bgcolor: TEAL_SOFT },
                          }}
                        >
                          Ver detalhes
                        </Button>
                        <Button
                          size="small"
                          endIcon={<ArrowIcon />}
                          onClick={() => navigate(modulo.path)}
                          sx={{
                            color: TEAL,
                            fontWeight: 700,
                            borderRadius: '5px',
                            '&:hover': { bgcolor: TEAL_SOFT },
                          }}
                        >
                          Acessar
                        </Button>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* CTA */}
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: TEXT, mb: 2 }}>
              Pronto para usar o ConectaChurch?
            </Typography>
            <Typography sx={{ color: MUTED, mb: 4, maxWidth: 500, mx: 'auto' }}>
              Acesse o sistema agora mesmo e comece a organizar a gestão da sua igreja.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                sx={{ bgcolor: TEAL, color: '#0B1200', fontWeight: 700, px: 4, borderRadius: '5px', '&:hover': { bgcolor: '#00D597' } }}
              >
                Acessar o sistema
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/meus-cursos')}
                sx={{ borderColor: BORDER_STRONG, color: MUTED, fontWeight: 700, px: 4, borderRadius: '5px', '&:hover': { borderColor: TEAL, bgcolor: TEAL_SOFT } }}
              >
                Portal do Aluno
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Modal de Detalhes ─────────────────────────────────── */}
      <Dialog
        open={modal.open}
        onClose={() => setModal({ open: false, modulo: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '5px', bgcolor: SURFACE, color: TEXT, overflow: 'hidden' } }}
      >
        {modal.modulo && (() => {
          const IconComp = modal.modulo.icone;
          return (
            <>
              <Box sx={{
                bgcolor: modal.modulo.corBg,
                borderBottom: `3px solid ${modal.modulo.cor}`,
                p: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '5px',
                    bgcolor: modal.modulo.cor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <IconComp sx={{ color: '#0B1200', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: TEXT }}>
                      {modal.modulo.titulo}
                    </Typography>
                    <Typography sx={{ color: modal.modulo.cor, fontWeight: 600 }}>
                      {modal.modulo.subtitulo}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setModal({ open: false, modulo: null })} sx={{ color: MUTED }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              <DialogContent sx={{ p: 4 }}>
                <Typography sx={{ color: MUTED, lineHeight: 1.7, mb: 3, fontSize: '1.05rem' }}>
                  {modal.modulo.descricao}
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 800, color: TEXT, mb: 2 }}>
                  Funcionalidades
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
                  {modal.modulo.beneficios.map((b, i) => (
                    <Chip
                      key={i}
                      icon={<CheckIcon sx={{ fontSize: 16, color: `${modal.modulo.cor} !important` }} />}
                      label={b}
                      size="small"
                      sx={{
                        bgcolor: modal.modulo.corBg,
                        color: modal.modulo.cor,
                        fontWeight: 600,
                        border: `1px solid ${modal.modulo.cor}40`,
                      }}
                    />
                  ))}
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 800, color: TEXT, mb: 1 }}>
                  Como funciona
                </Typography>
                <Typography sx={{ color: MUTED, lineHeight: 1.7, mb: 3 }}>
                  {modal.modulo.comoFunciona}
                </Typography>

                <Box sx={{
                  bgcolor: modal.modulo.corBg,
                  borderRadius: '5px',
                  p: 2,
                  borderLeft: `4px solid ${modal.modulo.cor}`,
                }}>
                  <Typography sx={{ fontWeight: 700, color: TEXT, mb: 0.5 }}>
                    Para quem é
                  </Typography>
                  <Typography sx={{ color: MUTED }}>
                    {modal.modulo.praQuem}
                  </Typography>
                </Box>
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button onClick={() => setModal({ open: false, modulo: null })} sx={{ color: MUTED, borderRadius: '5px' }}>
                  Fechar
                </Button>
                <Button
                  variant="contained"
                  onClick={() => { setModal({ open: false, modulo: null }); navigate(modal.modulo.path); }}
                  sx={{ bgcolor: modal.modulo.cor, color: '#0B1200', fontWeight: 700, borderRadius: '5px', '&:hover': { filter: 'brightness(0.9)' } }}
                  endIcon={<ArrowIcon />}
                >
                  Acessar Módulo
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <Footer />
    </>
  );
}
